import { PaymentSystemTransitionType } from 'containers/home/constants';
import {
  ConnectBankStatuses,
  EnablePayoutStatuses,
  InternalBankStatuses,
  PaymentSystem,
  PayoutScheduleType,
  PayoutType
} from 'containers/payouts/constants';

export enum VerificationStatusEnum {
  InReview = 'IN_REVIEW',
  NotVerified = 'NOT_VERIFIED',
  Verified = 'VERIFIED',
  MerchantOnHold = 'ON_HOLD',
  Rejected = 'REJECTED',
  AwaitingDocs = 'WAITING_FOR_DOCUMENTS',
  Declined = 'DECLINED'
}
export interface UserDetails {
  id: string;
  name: string;
  email: string;
  phone: string;
  deviceToken: string;
  isMerchantBlocked: boolean;
  earnedCredits: string;
  remainingCredits: string;
  verificationStatus: VerificationStatusEnum;
  isCustomerChampion: boolean;
  isSalesFunnelMerchant: boolean;
  deviceInfoRequired: boolean;
  preCreditFeature: boolean;
  defaultLandingPage: string;
  referAndEarnEnabled: boolean;
  isDeviceAuthenticated: boolean;
  isPasswordActive: boolean;
  isPasswordCreated: boolean;
  isEmailVerified: boolean;
  isEmailAdded: boolean;
  showEmailPrompt: boolean;
  showVerificationPrompt: boolean;
  skipIamFlow: boolean;
  skippedAuthentication: boolean; // This becomes true if once the user logs in
  brbClosureDate: string;
  faqMigrationSafeDate: string;
  faqMigrationMonthAndYear: string;
  createNewTeam: boolean;
  additionalConfig: {
    saveLineItemToCatalogue: boolean;
  };
}

export interface GetMerchantTeamsResponse {
  merchantTeams: MerchantTeam[];
}

export interface AutoBlockInfo {
  title: string;
  subtitle: string;
  description: string;
}

export interface MerchantTeam {
  id: number;
  name: string;
  role: TeamRole;
  profileImageUrl: string;
  permission: Permission;
  storeName: string;
  profession: string;
  integrationsEnabled: boolean;
  quickBooksIntegrationEnabled: boolean;
  connectIntegrationsEnabled: boolean;
  connectApplicationStatus: EnablePayoutStatuses;
  isRiskReviewDone?: boolean;
  connectBankingStatus: ConnectBankStatuses;
  internalBankingStatus: InternalBankStatuses;
  paymentSystemTransitionType: PaymentSystemTransitionType;
  teamMerchantName: string;
  subscriptionStatus: SubscriptionStatus;
  isSubscriptionCreatedFromWeb: boolean;
  isTeamBlocked: boolean;
  isMerchantBlockedInTeam: boolean;
  isTemporarilyBlocked: boolean;
  autoBlockInfo?: AutoBlockInfo;
  lendicaIntegrationsEnabled?: boolean;
  isUnitEnabled?: boolean;
  unitProvisionStatus?: string;
}

export enum SubscriptionStatus {
  SUBSCRIBED = 'SUBSCRIBED',
  NOT_SUBSCRIBED = 'NOT_SUBSCRIBED',
  SUBSCRIPTION_EXPIRED = 'SUBSCRIPTION_EXPIRED',
  SUBSCRIPTION_CANCELLED = 'SUBSCRIPTION_CANCELED',
  EXPIRED = 'Expired',
  CANCELLED = 'Cancelled'
}

export enum TeamRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  CUSTOM = 'custom',
  SUPER_ADMIN = 'super-admin',
  DISPATCH_ADMIN = 'dispatch-admin'
}

export enum PermissionLevels {
  EDIT_ALL = 'Edit all',
  EDIT_SELF_ONLY = 'Edit self only',
  VIEW_ONLY = 'View only',
  FULL_ACCESS = 'Full access',
  NO_ACCESS = 'No access',
  VIEW_ACCOUNT_ONLY = 'View account only',
  CUSTOM = 'Custom',
  MANAGE_FUNDS = 'Manage Funds'
}

export interface Permission {
  role: TeamRole;
  permissions: Permissions;
}

export interface Permissions {
  teamSettings: TeamSettings;
  invoice: InvoiceSettings;
  fees: FeesSettings;
  customers: CustomersSettings;
  banking: BankingSettings;
  refund: RefundSettings;
  job: JobSettings;
  accounting: AccountingSettings;
  reports: ReportsSettings;
}

export interface TeamSettings {
  level: PermissionLevels;
  teamSettingPermissionList: TeamSettingPermissionList;
}

export interface TeamSettingPermissionList {
  viewTeamProfile: boolean;
  editTeamProfile: boolean;
  inviteNewMember: boolean;
  editExistingMember: boolean;
  viewExistingMember: boolean;
}

export interface InvoiceSettings {
  level: PermissionLevels;
  invoicePermissionList: InvoicePermissionList;
}

export interface InvoicePermissionList {
  createInvoice: boolean;
  viewReceiptsByTeam: boolean;
  viewReceiptsBySelf: boolean;
  viewInvoicesByTeam: boolean;
  viewInvoicesBySelf: boolean;
  viewPayoutsBySelf: boolean;
  viewPayoutsByTeam: boolean;
}

export interface FeesSettings {
  level: PermissionLevels;
  feesPermissionList: FeesPermissionList;
}

export interface FeesPermissionList {
  manageItemsAndCategories: boolean;
  manageTaxes: boolean;
  manageServiceCharges: boolean;
  viewServiceFeeSettings: boolean;
  editServiceFeeSettings: boolean;
  viewTipsSettings: boolean;
  editTipsSettings: boolean;
  viewDiscount: boolean;
  editDiscount: boolean;
  viewTnc: boolean;
  editTnc: boolean;
  viewSignatureSettings: boolean;
  editSignatureSettings: boolean;
}

export interface CustomersSettings {
  level: PermissionLevels;
  customersPermissionList: CustomersPermissionList;
}

export interface CustomersPermissionList {
  createNewCustomers: boolean;
  editExistingCustomers: boolean;
  viewCustomersCreatedByTeam: boolean;
  editCustomersCreatedByTeam: boolean;
  viewCustomerCards: boolean;
  createCustomerCards: boolean;
  editCustomerCards: boolean;
}

export interface BankingSettings {
  level: PermissionLevels;
  bankingPermissionList: BankingPermissionList;
}

export interface BankingPermissionList {
  viewAccountDetails: boolean;
  createACHTransfer: boolean;
  createPayee: boolean;
  editExistingPayee: boolean;
  downloadStatement: boolean;
  viewCardDetails: boolean;
  manageCards: boolean;
  viewCashbackDetails: boolean;
}

export interface RefundSettings {
  level: PermissionLevels;
  refundPermissionList: RefundPermissionList;
}

export interface RefundPermissionList {
  createRefundsBySelf: boolean;
  createRefundsByTeam: boolean;
  viewDisputes: boolean;
  respondToDisputes: boolean;
}

export interface JobSettings {
  level: PermissionLevels;
  jobsPermissionList: JobsPermissionList;
}

export interface JobsPermissionList {
  createAndEditJob: boolean;
  manageOthersJobs: boolean;
  closeOrMarkJobAsDone: boolean;
  modifyDoneJobs: boolean;
  viewJobSettings: boolean;
  editJobSettings: boolean;
  viewJobSource: boolean;
  editJobSource: boolean;
}

export interface AccountingSettings {
  level: PermissionLevels;
  accountingPermissionList: AccountingPermissionList;
}

export interface AccountingPermissionList {
  viewItemCost: boolean;
  editItemCost: boolean;
  createExpenses: boolean;
  manageOthersExpenses: boolean;
  viewMyJobCommissionsAndPay: boolean;
  managePayAdjustments: boolean;
  overrideJobRatesAndCommissions: boolean;
  viewJobProfitability: boolean;
  viewAccountingSettings: boolean;
  editAccountingSettings: boolean;
  accessTeamAccounting: boolean;
  approveAndProcessAccounting: boolean;
}

export interface ReportsSettings {
  level: PermissionLevels;
  reportsPermissionList: ReportsPermissionList;
}

export interface ReportsPermissionList {
  accessReports: boolean;
}

export interface ToastNotification {
  message: ToastMessage;
  //TODO: change type to NotifierTypes
  type: string;
  duration?: number;
  id?: string;
  showClose?: boolean;
  bgStyle?: string;
  fontStyle?: string;
  shouldAnimate?: boolean;
}

interface ToastMessage {
  primaryMessage: string;
  secondaryMessage?: string;
  actionMessage?: string;
  actionKey?: string;
  iconInitials?: string;
  iconSrc?: string;
  isMessageHtml?: boolean;
}

interface ErrorData {
  code: number;
  details: string;
  message: string;
}

export interface ErrorResponse {
  status: number;
  data: ErrorData;
}

export const memberRolePermissions = {
  teamSettings: {
    level: PermissionLevels.VIEW_ONLY,
    teamSettingPermissionList: {
      viewTeamProfile: true,
      viewExistingMember: true,
      inviteNewMember: false,
      editTeamProfile: false,
      editExistingMember: false
    }
  },
  invoice: {
    level: PermissionLevels.EDIT_SELF_ONLY,
    invoicePermissionList: {
      createInvoice: true,
      viewReceiptsByTeam: false,
      viewReceiptsBySelf: true,
      viewInvoicesByTeam: false,
      viewInvoicesBySelf: true,
      viewPayoutsBySelf: true,
      viewPayoutsByTeam: false
    }
  },
  fees: {
    level: PermissionLevels.VIEW_ONLY,
    feesPermissionList: {
      manageItemsAndCategories: false,
      manageTaxes: false,
      manageServiceCharges: false,
      editServiceFeeSettings: false,
      editTipsSettings: false,
      viewTipsSettings: true,
      viewServiceFeeSettings: true,
      viewDiscount: true,
      editDiscount: false,
      viewTnc: true,
      editTnc: false,
      viewSignatureSettings: true,
      editSignatureSettings: false
    }
  },
  customers: {
    level: PermissionLevels.EDIT_SELF_ONLY,
    customersPermissionList: {
      createNewCustomers: true,
      editCustomersCreatedByTeam: false,
      editExistingCustomers: true,
      viewCustomersCreatedByTeam: true,
      viewCustomerCards: true,
      createCustomerCards: false,
      editCustomerCards: false
    }
  },
  banking: {
    level: PermissionLevels.NO_ACCESS,
    bankingPermissionList: {
      createPayee: false,
      createACHTransfer: false,
      viewCashbackDetails: false,
      viewCardDetails: false,
      viewAccountDetails: false,
      editExistingPayee: false,
      downloadStatement: false,
      manageCards: false
    }
  },
  refund: {
    level: PermissionLevels.EDIT_SELF_ONLY,
    refundPermissionList: {
      createRefundsByTeam: false,
      createRefundsBySelf: true,
      viewDisputes: true,
      respondToDisputes: true
    }
  },
  job: {
    level: PermissionLevels.EDIT_SELF_ONLY,
    jobsPermissionList: {
      createAndEditJob: true,
      manageOthersJobs: false,
      closeOrMarkJobAsDone: false,
      modifyDoneJobs: false,
      viewJobSettings: true,
      editJobSettings: false,
      viewJobSource: false,
      editJobSource: false
    }
  },
  accounting: {
    level: PermissionLevels.EDIT_SELF_ONLY,
    accountingPermissionList: {
      viewItemCost: true,
      editItemCost: false,
      createExpenses: true,
      manageOthersExpenses: false,
      viewMyJobCommissionsAndPay: true,
      managePayAdjustments: false,
      overrideJobRatesAndCommissions: false,
      viewJobProfitability: false,
      viewAccountingSettings: false,
      editAccountingSettings: false,
      accessTeamAccounting: false,
      approveAndProcessAccounting: false
    }
  },
  reports: {
    level: PermissionLevels.NO_ACCESS,
    reportsPermissionList: {
      accessReports: false
    }
  }
};

export interface PaymentSystemConfigResponse {
  paymentSystem: PaymentSystem;
  payoutType: PayoutType;
  payoutSchedule: PayoutScheduleType;
  bankingEnabled: boolean;
}

export enum PermissionTypeEnum {
  TEAMSETTINGS = 'teamSettings',
  INVOICE = 'invoice',
  FEES = 'fees',
  CUSTOMERS = 'customers',
  BANKING = 'banking',
  REFUND = 'refund',
  JOBS = 'job',
  ACCOUNTING = 'accounting',
  REPORTS = 'reports'
}

export interface TeamBankAccountDetails {
  bankName: string;
  bankIcon: string;
  accountNumber: string;
  routingNumber: string;
  isScanpayAccount: boolean;
  paymentMethod: string;
}

export interface Banner {
  id: string;
  title: string;
  sub_title: string;
  cta_text: string;
  cta_type: string;
  cta_action: string;
  webview_title: string;
  webview_source: string;
  image_url: string;
  colours: string[];
  cta_text_colour: string;
  cta_bg_colour: string;
}

export interface GetTeamBannersResponse {
  data: {
    payout_banners: Banner[];
    home_banners: Banner[];
  };
}

export interface TeamBanners {
  payoutBanners: Banner[];
  homeBanners: Banner[];
}

export interface SubscriptionActivityPayload {
  feature: string;
  mobileEnabledNowClicked?: boolean;
  webFreePlanSelected: boolean;
  webPaidPlanSelected: boolean;
  webPaidPlanDuration?: number;
  webPaidPlanCancelled?: boolean;
}

export interface SubscriptionActivityResponse {
  success: boolean;
  message?: string;
  // Add more fields if the API returns additional data
}

export interface CommissionBaseItem {
  key: string;
  label: string;
  tooltip: string;
  mobToolTip: string;
  moreInfo?: string;
  isEnabledForCommissions: boolean;
  isEnabledForReferrals: boolean;
}

export type CommissionBaseMap = Record<string, CommissionBaseItem>;

export interface GetCommissionBaseResponse {
  commissionBaseMap: CommissionBaseMap;
  commissionBaseList: CommissionBaseItem[];
}

export interface EntityFilterType {
  key: string;
  label: string;
  isEnabled: boolean;
}

export interface EntityFilterTab {
  label: string;
  applyOn: string[];
  isEnabled: boolean;
}

export interface PayoutTransactionFilters {
  filters: {
    byType: EntityFilterType[];
  };
  tabs: EntityFilterTab[];
}

export interface EntityFilters {
  payoutTransactions: PayoutTransactionFilters;
}

export interface GetEntityFiltersResponse {
  entityFilters: EntityFilters;
}

export interface ChargeCustomerCardPayload {
  qrId: string;
  amount: string;
  serviceChargeAmount: string;
  customerCardId: string;
  customerInfo: ChargeCustomerCardCustomerInfoBase;
}

export interface ChargeCustomerCardCustomerInfo extends ChargeCustomerCardCustomerInfoBase {
  cardId: string;
}

export interface ChargeCustomerCardCustomerInfoBase {
  email: string;
  name: string;
  country: string;
}

export interface ChargeCustomerCardSuccessResponse {
  merchantId: string;
  transactionId: string;
  clientSecret: string;
  checkoutId: string;
  returningCustomer: boolean;
  customerInfo: ChargeCustomerCardCustomerInfo;
}

export interface ChargeCustomerCardErrorResponse {
  code: number;
  details: string;
  message: string;
}

export interface ChargeCustomerCardResponse {
  success: boolean;
  data: ChargeCustomerCardSuccessResponse | ChargeCustomerCardErrorResponse;
}

export interface ChargeCustomerCardErrorPayload {
  transactionId: string;
  errorCode: string;
}

export interface ChargeCustomerCardErrorStatusResponse {
  title: string;
  subtitle: string;
  canRetry: boolean;
}

export interface CustomerCareDetails {
  customerCarePhone: string;
  customerCareEmail: string;
  csmDetails: {
    name: string;
    phone: string;
    avatar: string;
    meetingLink: string;
  };
}

export interface GetMerchantMessagesResponse {
  messages: Record<string, string>;
}

export type MerchantMessages = Record<string, string>;
